import { z } from 'zod';
/**
 * Roles are stored as custom claims on the Auth user and mirrored onto the user
 * document. The claim is the enforcement source of truth; the mirror exists so
 * that Firestore queries can filter by role. They must never diverge — only the
 * server writes either one, and it writes both in the same operation.
 */
export const roleSchema = z.enum([
    'admin',
    /**
     * Company commander (מפקד פלוגה, CC) and company executive officer (סגן
     * מפקד פלוגה, CXO) — owner, 2026-09-14, story 35. For now each has the
     * CSM's reach over gear *and* the CHRS's reach over people; neither
     * changes roles or the org structure, which stays the admin's.
     */
    'company_commander',
    'company_xo',
    'csm',
    /** Assistant CSM (סגן רס"פ; owner, 2026-09-14, story 36): the CSM's reach over gear, as its own role. */
    'acsm',
    /**
     * Personnel, company-wide (owner, 2026-09-13): reads every soldier's file,
     * edits personal details, and approves soldiers' own change requests. No
     * gear: cannot issue, return, transfer or touch the storeroom.
     */
    'chrs',
    'platoon_commander',
    /**
     * Platoon sergeant (owner, 2026-09-13, story 30): everything the platoon
     * commander may do, plus the platoon store — signs gear from the company
     * store under their own name and distributes it to the platoon's soldiers,
     * who sign against the sergeant and return to the sergeant.
     */
    'platoon_sergeant',
    'team_commander',
    'soldier',
]);
/** Company command: the commander and the XO, above the CSM and the CHRS in both of their reaches. */
export const COMPANY_COMMAND_ROLES = ['company_commander', 'company_xo'];
/**
 * Every role with the storeroom's reach over gear — the whole company's
 * catalog, inventory, issuing, returns, write-offs. `isCompanyWide` in the
 * functions and `companyWide()` in the rules are this list.
 */
export const GEAR_WIDE_ROLES = ['admin', ...COMPANY_COMMAND_ROLES, 'csm', 'acsm'];
/**
 * Every role that sees and edits every *person* in the company: the
 * personnel side, never gear by itself. `isPeopleWide` / `peopleWide()`.
 */
export const PEOPLE_WIDE_ROLES = ['admin', ...COMPANY_COMMAND_ROLES, 'chrs'];
export function isGearWide(roles) {
    return roles.some((r) => GEAR_WIDE_ROLES.includes(r));
}
export function isPeopleWideRole(roles) {
    return roles.some((r) => PEOPLE_WIDE_ROLES.includes(r));
}
/** The roles that lead a platoon: same scope, one of them also runs the platoon store. */
export const PLATOON_LEADER_ROLES = ['platoon_commander', 'platoon_sergeant'];
export function leadsPlatoon(roles) {
    return roles.some((r) => PLATOON_LEADER_ROLES.includes(r));
}
/**
 * Numeric level per REQUIREMENTS §2.2. Used for "must be above both parties"
 * style checks (e.g. controlled-item transfer approval). A user holds several
 * roles; their effective level is the maximum.
 */
export const ROLE_LEVEL = {
    admin: 3,
    company_commander: 3,
    company_xo: 3,
    csm: 3,
    acsm: 3,
    chrs: 2,
    platoon_commander: 2,
    platoon_sergeant: 2,
    team_commander: 1,
    soldier: 0,
};
export function effectiveLevel(roles) {
    return roles.reduce((max, role) => Math.max(max, ROLE_LEVEL[role]), -1);
}
export const MAX_ROLE_LEVEL = 3;
/**
 * The level a transfer's approver must hold (owner, 2026-09-21): one above
 * the highest level among everyone involved — giver, receiver, initiator.
 * Nothing sits above level 3, so a transaction that already reaches the top
 * is signed off by another level-3 holder who is not one of the parties.
 */
export function transferApprovalLevel(partyLevels) {
    return Math.min(MAX_ROLE_LEVEL, Math.max(0, ...partyLevels) + 1);
}
export function hasRole(roles, role) {
    return roles.includes(role);
}
/** Parse an unknown claim value into roles, dropping anything unrecognised. */
export function parseRoles(value) {
    const result = z.array(roleSchema).safeParse(value);
    return result.success ? result.data : [];
}
//# sourceMappingURL=roles.js.map